import { useCallback, useRef, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { SpaceDocument } from 'types/space';
import type { CompositionRef } from 'types/composition';
import {
  verifyArtifact,
  CompositionResolutionUnavailableError,
  type ConformanceFinding,
  type ConformanceLevel,
} from '@center-platform/space-format/conformance';
import { CURRENT_CATALOG_VERSION } from '@center-platform/space-format/catalog';
import { loadClientCompositionCache, createClientCompositionResolver, isCatalogPrimitive } from './composerCheckResolver';

const CHECK_LEVELS: ConformanceLevel[] = ['L0', 'L1', 'L2', 'L3'];

export interface ComposerCheckResult {
  findings: ConformanceFinding[];
  levelsRun: ConformanceLevel[];
  checkedAt: number;
  /**
   * The check could NOT run because a composition reference could not be looked up (5xx, network).
   * Distinct from "ran and found nothing": reporting a transport failure as zero findings would
   * tell the author their draft is clean, and reporting it as `REF_UNRESOLVED` would tell them it
   * is broken. Neither is true, so the panel says the check is unavailable.
   */
  unavailable?: boolean;
}

export interface UseComposerCheckResult {
  result: ComposerCheckResult | null;
  checking: boolean;
  /**
   * Runs `verifyArtifact` L0-L3 against `document` — the same function the write routes, publish
   * gate, composer-agent's `propose_space_edit` tool result, and fleet sweep all run (SC3), so this
   * panel's finding codes are never a bespoke subset. On-demand rather than on every keystroke: a
   * transitive composition-reference warm-up is a real network round-trip, not a pure computation.
   * Never rejects for a failed reference lookup — it resolves with `unavailable: true` instead.
   */
  runCheck: () => Promise<ComposerCheckResult>;
}

/**
 * The composer check panel's `verifyArtifact` runner — client-side counterpart to
 * composer-agent-service's `handleProposeSpaceEdit` (same `verifyArtifact` call, same finding
 * codes), warmed via `composerCheckResolver.ts`'s browser-side BFS since `verifyArtifact` needs a
 * synchronous resolver and this service has no direct DB access. No `schemaSystemName` is threaded
 * through — L3 (data-contract) is a documented no-op per `VerifyArtifactContext.schemaSystemName`'s
 * contract until a sphere-scoped schema anchor is wired to the composer shell, not a gap.
 */
export function useComposerCheck(document: SpaceDocument, apiClient: ApiClient | undefined): UseComposerCheckResult {
  const [result, setResult] = useState<ComposerCheckResult | null>(null);
  const [checking, setChecking] = useState(false);
  // Guards against a stale response landing after a newer check started (rapid double-click).
  const runIdRef = useRef(0);

  const runCheck = useCallback(async (): Promise<ComposerCheckResult> => {
    const runId = ++runIdRef.current;
    setChecking(true);
    try {
      const resolveComposition = apiClient
        ? createClientCompositionResolver(await loadClientCompositionCache(apiClient, document.a2ui))
        : (ref: CompositionRef) =>
            isCatalogPrimitive(ref.systemName) ? ({ status: 'primitive' as const }) : ({ status: 'unresolved' as const });
      const { findings, levelsRun } = verifyArtifact(
        { artifactType: 'space', document },
        { resolveComposition, catalogVersion: CURRENT_CATALOG_VERSION },
        { levels: CHECK_LEVELS }
      );
      const next: ComposerCheckResult = { findings, levelsRun, checkedAt: Date.now() };
      if (runIdRef.current === runId) setResult(next);
      return next;
    } catch (err) {
      if (!(err instanceof CompositionResolutionUnavailableError)) throw err;
      const next: ComposerCheckResult = { findings: [], levelsRun: [], checkedAt: Date.now(), unavailable: true };
      if (runIdRef.current === runId) setResult(next);
      return next;
    } finally {
      if (runIdRef.current === runId) setChecking(false);
    }
  }, [apiClient, document]);

  return { result, checking, runCheck };
}
